'use strict';

var os = require('node:os');
var process = require('node:process');
var path = require('node:path');
var which = require('which');
var fs = require('node:fs');
var RegExp = require('@ericcornelissen/lregexp');
var node_util = require('node:util');

function _interopNamespaceDefault(e) {
  var n = Object.create(null);
  if (e) {
    Object.keys(e).forEach(function (k) {
      if (k !== 'default') {
        var d = Object.getOwnPropertyDescriptor(e, k);
        Object.defineProperty(n, k, d.get ? d : {
          enumerable: true,
          get: function () { return e[k]; }
        });
      }
    });
  }
  n.default = e;
  return Object.freeze(n);
}

var path__namespace = /*#__PURE__*/_interopNamespaceDefault(path);
var fs__namespace = /*#__PURE__*/_interopNamespaceDefault(fs);

/**
 * @overview Provides functionality to compose escaping, quoting, and flag
 * protection functions.
 * @license MPL-2.0
 */

/**
 * Compose escape, flag protection, and quoting functions to create a function
 * to be used for escaping shell arguments.
 *
 * If no `flagFn` or `quoteFn` is provided the respective functionality is
 * omitted from the resulting function.
 *
 * @param {object} fns The functions to compose.
 * @param {function(string): string} fns.escapeFn An argument escaper.
 * @param {function(string): string[]} [fns.flagFn] The flag-based splitter.
 * @param {function(string): string} [fns.quoteFn] An argument quoter.
 * @returns {function(string): string} A function to escape shell arguments.
 */
function compose({ escapeFn, flagFn, quoteFn }) {
  const escape = quoteFn
    ? (arg) => quoteFn(escapeFn(arg))
    : (arg) => escapeFn(arg);

  if (!flagFn) {
    return escape;
  }

  return (arg) => {
    let [preFlag, , ...rest] = flagFn(arg);
    while (rest.length > 0 && escapeFn(preFlag) === "") {
      arg = rest.join("");
      [preFlag, , ...rest] = rest;
    }

    return escape(arg);
  };
}

/**
 * @overview Provides reflection functionality.
 * @license MPL-2.0
 */

/**
 * The error message for non-array values.
 *
 * @constant
 * @type {string}
 */
const typeErrorArray = "Shescape requires argument lists to be an array";

/**
 * The error message for non-stringable values.
 *
 * @constant
 * @type {string}
 */
const typeErrorString =
  "Shescape requires strings or values that can be converted into a string using .toString()";

/**
 * The `typeof` value of functions.
 *
 * @constant
 * @type {string}
 */
const typeofFunction = "function";

/**
 * The `typeof` value of strings.
 *
 * @constant
 * @type {string}
 */
const typeofString = "string";

/**
 * Check if the given object has the given property as an own property.
 *
 * This custom function is used over `Object.hasOwn` because that isn't
 * available in all supported Node.js versions.
 *
 * @param {object} object The object of interest.
 * @param {string} property The property of interest.
 * @returns {boolean} `true` if property is an own-property, `false` otherwise.
 */
function hasOwn(object, property) {
  return Object.prototype.hasOwnProperty.call(object, property);
}

/**
 * Checks if a value can be converted into a string and converts it if possible.
 *
 * @param {unknown} value The value of interest.
 * @returns {string | null} If possible the string of `value`, otherwise `null`.
 */
function maybeToString(value) {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value.toString !== typeofFunction) {
    return null;
  }

  const maybeStr = value.toString();
  if (!isString(maybeStr)) {
    return null;
  }

  return maybeStr;
}

/**
 * Convert a value into a string if that is possible.
 *
 * @param {unknown} value The value to convert into a string.
 * @returns {string} The `value` as a string.
 * @throws {TypeError} The `value` is not stringable.
 */
function checkedToString(value) {
  if (isString(value)) {
    return value;
  }

  const maybeStr = maybeToString(value);
  if (maybeStr === null) {
    throw new TypeError(typeErrorString);
  }

  return maybeStr;
}

/**
 * Ensures a value is an array.
 *
 * @param {unknown} value The value of interest.
 * @throws {TypeError} The `value` is not an array.
 */
function ensureArray(value) {
  if (!isArray(value)) {
    throw new TypeError(typeErrorArray);
  }
}

/**
 * Checks if a value is a string.
 *
 * @param {unknown} value The value of interest.
 * @returns {boolean} `true` if `value` is a string, `false` otherwise.
 */
function isString(value) {
  return typeof value === typeofString;
}

/**
 * Checks if a value is an array.
 *
 * @param {unknown} value The value of interest.
 * @returns {boolean} `true` if `value` is an array, `false` otherwise.
 */
const isArray = Array.isArray;

/**
 * @overview Provides functionality related to working with executables.
 * @license MPL-2.0
 */


/**
 * Build error messages for when executables cannot be found.
 *
 * @param {string} executable The executable being looked up.
 * @returns {string} The executable not found error message.
 */
function notFoundError(executable) {
  return `No executable could be found for ${executable}`;
}

/**
 * Resolves the location of an executable given an arbitrary valid string
 * representation of that executable.
 *
 * To obtain the location of the executable this function (if necessary):
 * - Expands the provided string to an absolute path.
 * - Follows symbolic links.
 *
 * @param {object} args The arguments for this function.
 * @param {Object<string, string>} args.env The environment variables.
 * @param {string} args.executable A string representation of the executable.
 * @param {object} deps The dependencies for this function.
 * @param {function(): boolean} deps.exists A function to check if a file exists.
 * @param {function(): string} deps.readlink A function to resolve (sym)links.
 * @param {function(): string} deps.which A function to perform a `which(1)`-like lookup.
 * @returns {string} The full path to the binary of the executable.
 * @throws {Error} If the executable could not be found.
 */
function resolveExecutable(
  { env, executable },
  { exists, readlink, which },
) {
  let resolved = executable;
  try {
    const path = hasOwn(env, "PATH")
      ? env.PATH
      : hasOwn(env, "Path")
        ? env.Path
        : undefined;
    resolved = which(resolved, { path });
  } catch {
    throw new Error(notFoundError(executable));
  }

  if (!exists(resolved)) {
    throw new Error(notFoundError(executable));
  }

  try {
    const seen = {};
    while (!hasOwn(seen, resolved)) {
      seen[resolved] = null;
      const link = readlink(resolved);
      const base = path__namespace.dirname(resolved);
      resolved = path__namespace.resolve(base, link);
    }
  } catch {
    // An error is thrown if the argument is not a (sym)link, this is what we
    // want so we return.
    return resolved;
  }

  throw new Error(`${executable} points to a link loop, cannot resolve shell`);
}

/**
 * @overview Provides functionality for parsing shescape options.
 * @license MPL-2.0
 */


/**
 * The identifier for 'no shell' or the absence of a shell.
 *
 * @constant
 * @type {symbol}
 */
const noShell = Symbol();

/**
 * Check if the Node.js version is before v22.0.0.
 *
 * @param {string} version The `process.version` value.
 * @returns {boolean} `true` if version is before v22.0.0, `false` otherwise.
 */
function isBeforeNode22(version) {
  const [major] = version.replace("v", "").split(".");
  return Number.parseInt(major, 10) < 22; // eslint-disable-line no-magic-numbers
}

/**
 * Parses the `flagProtection` option.
 *
 * @param {object} args The arguments for this function.
 * @param {object} args.options The options for escaping.
 * @returns {object} The parsed option.
 */
function parseFlagProtection({ options }) {
  const flagProtection = hasOwn(options, "flagProtection")
    ? options.flagProtection
    : undefined;
  if (flagProtection === undefined) {
    return true;
  }

  return Boolean(flagProtection);
}

/**
 * Parses the `shell` option.
 *
 * @param {object} args The arguments for this function.
 * @param {Object<string, string>} args.env The environment variables.
 * @param {object} args.options The options for escaping.
 * @param {string} args.version The Node.js version.
 * @param {object} deps The dependencies for this function.
 * @param {function(): string} deps.getDefaultShell Function to get the default shell.
 * @param {function(): string} deps.getShellName Function to get the name of a shell.
 * @param {function(): boolean} deps.isShellSupported Function to see if a shell is usable.
 * @returns {object} The parsed option.
 * @throws {Error} The shell is not supported or could not be found.
 */
function parseShell(
  { env, options, version },
  { getDefaultShell, getShellName, isShellSupported },
) {
  let shell =
    hasOwn(options, "shell") || isBeforeNode22(version)
      ? options.shell
      : undefined;
  let shellName = noShell;

  if (shell !== false) {
    if (!isString(shell)) {
      shell = getDefaultShell({ env });
    }

    shellName = getShellName({ env, shell }, { resolveExecutable });
  }

  if (!isShellSupported(shellName)) {
    throw new Error(`Shescape does not support the shell ${shellName}`);
  }

  return shellName;
}

/**
 * Parses the options provided to shescape.
 *
 * @param {object} args The arguments for this function.
 * @param {Object<string, string>} args.env The environment variables.
 * @param {object} args.options The options for escaping.
 * @param {string} args.version The Node.js version.
 * @param {object} deps The dependencies for this function.
 * @param {function(): string} deps.getDefaultShell Function to get the default shell.
 * @param {function(): string} deps.getShellName Function to get the name of a shell.
 * @param {function(): boolean} deps.isShellSupported Function to see if a shell is usable.
 * @returns {object} The parsed options.
 * @throws {Error} The shell is not supported or could not be found.
 */
function parseOptions(args, deps) {
  const flagProtection = parseFlagProtection(args);
  const shellName = parseShell(args, deps);
  return { flagProtection, shellName };
}

/**
 * @overview Provides an interface for interacting with the file system.
 * @license MPL-2.0
 */


/**
 * Synchronously look up if the `path` exists.
 *
 * @param {string} path The path to look up.
 * @param {object} [deps] Node.js' fs module.
 * @returns {boolean} `true` if the path exists, `false` otherwise.
 */
function existsSync(path, deps = fs__namespace) {
  return deps.existsSync(path);
}

/**
 * Synchronously look up the (target) location of a symbolic link.
 *
 * @param {string} path The path to look up.
 * @param {object} [deps] Node.js' fs module.
 * @returns {string} The contents of `path`.
 * @throws {Error} If `path` is not a symbolic link.
 */
function readlinkSync(path, deps = fs__namespace) {
  return deps.readlinkSync(path);
}

/**
 * @overview Provides functionality for the Bourne-again shell (Bash).
 * @license MPL-2.0
 */


/**
 * Returns a function to escape arguments for use in Bash for the given use
 * case.
 *
 * @returns {function(string): string} A function to escape arguments.
 */
function getEscapeFunction$8() {
  const controls = new RegExp(/[\0\u0008\r\u001B\u009B]/g);
  const newlines = new RegExp(/\n/g);
  const backslashes = new RegExp(/\\/g);
  const comments = new RegExp(/(^|\s)#/g);
  const home = new RegExp(/(^|[\s:=])~/g);
  const specials = new RegExp(/(["$&'()*;<>?[\]`{|])/g);
  const whitespace = new RegExp(/([\t ])/g);
  return (arg) =>
    arg
      .replace(controls, "")
      .replace(newlines, " ")
      .replace(backslashes, "\\\\")
      .replace(comments, "$1\\#")
      .replace(home, "$1\\~")
      .replace(specials, "\\$1")
      .replace(whitespace, "\\$1");
}

/**
 * Escape an argument for use in Bash when the argument is being quoted.
 *
 * @returns {function(string): string} A function to escape arguments.
 */
function getQuoteEscapeFunction$4() {
  const controls = new RegExp(/[\0\u0008\u001B\u009B]/g);
  const crs = new RegExp(/(\r\n)|\r/g);
  const quotes = new RegExp(/'/g);
  return (arg) =>
    arg.replace(controls, "").replace(crs, "$1").replace(quotes, "'\\''");
}

/**
 * Quotes an argument for use in Bash.
 *
 * @param {string} arg The argument to quote.
 * @returns {string} The quoted argument.
 */
function quoteArg$6(arg) {
  return `'${arg}'`;
}

/**
 * Returns a pair of functions to escape and quote arguments for use in Bash.
 *
 * @returns {(function(string): string)[]} A function pair to escape & quote arguments.
 */
function getQuoteFunction$8() {
  return [getQuoteEscapeFunction$4(), quoteArg$6];
}

var bash = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getEscapeFunction: getEscapeFunction$8,
  getQuoteFunction: getQuoteFunction$8
});

/**
 * @overview Provides functionality for the BusyBox shell.
 * @license MPL-2.0
 */


/**
 * Returns a function to escape arguments for use in BusyBox for the given use
 * case.
 *
 * @returns {function(string): string} A function to escape arguments.
 */
function getEscapeFunction$7() {
  const controls = new RegExp(/[\0\u0008\r\u001B\u009B]/g);
  const newlines = new RegExp(/\n/g);
  const backslashes = new RegExp(/\\/g);
  const comments = new RegExp(/(^|\s)#/g);
  const home = new RegExp(/(^|\s)~/g);
  const specials = new RegExp(/(["$&'()*;<>?[\]`|])/g);
  const whitespace = new RegExp(/([\t ])/g);
  return (arg) =>
    arg
      .replace(controls, "")
      .replace(newlines, " ")
      .replace(backslashes, "\\\\")
      .replace(comments, "$1\\#")
      .replace(home, "$1\\~")
      .replace(specials, "\\$1")
      .replace(whitespace, "\\$1");
}

/**
 * Escape an argument for use in BusyBox when the argument is being quoted.
 *
 * @returns {function(string): string} A function to escape arguments.
 */
function getQuoteEscapeFunction$3() {
  const controls = new RegExp(/[\0\u0008\u001B\u009B]/g);
  const crs = new RegExp(/(\r\n)|\r/g);
  const quotes = new RegExp(/'/g);
  return (arg) =>
    arg.replace(controls, "").replace(crs, "$1").replace(quotes, "'\\''");
}

/**
 * Quotes an argument for use in BusyBox.
 *
 * @param {string} arg The argument to quote.
 * @returns {string} The quoted argument.
 */
function quoteArg$5(arg) {
  return `'${arg}'`;
}

/**
 * Returns a pair of functions to escape and quote arguments for use in BusyBox.
 *
 * @returns {(function(string): string)[]} A function pair to escape & quote arguments.
 */
function getQuoteFunction$7() {
  return [getQuoteEscapeFunction$3(), quoteArg$5];
}

var busybox = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getEscapeFunction: getEscapeFunction$7,
  getQuoteFunction: getQuoteFunction$7
});

/**
 * @overview Provides functionality for the C shell (csh).
 * @license MPL-2.0
 */


/**
 * Returns a function to escape arguments for use in csh for the given use case.
 *
 * @returns {function(string): string} A function to escape arguments.
 */
function getEscapeFunction$6() {
  const controls = new RegExp(/[\0\u0008\r\u001B\u009B]/g);
  const newlines = new RegExp(/\n/g);
  const backslashes = new RegExp(/\\/g);
  const home = new RegExp(/(^|\s)~/g);
  const history = new RegExp(/!/g);
  const specials = new RegExp(/(["#$&'()*;<>?[`{|])/g);
  const whitespace = new RegExp(/([\t ])/g);

  const textEncoder = new node_util.TextEncoder();
  return (arg) =>
    arg
      .replace(controls, "")
      .replace(newlines, " ")
      .replace(backslashes, "\\\\")
      .replace(home, "$1\\~")
      .replace(history, "\\!")
      .replace(specials, "\\$1")
      .replace(whitespace, "\\$1")
      .split("")
      .map(
        // Due to a bug in C shell version 20110502-7, when a character whose
        // utf-8 encoding includes the bytes 0xA0 (160 in decimal) appears in
        // an argument after an escaped character, it will hang and endlessly
        // consume memory unless the character is escaped with quotes.
        // ref: https://bugs.debian.org/cgi-bin/bugreport.cgi?bug=995013
        (char) => (textEncoder.encode(char).includes(160) ? `'${char}'` : char),
      )
      .join("");
}

/**
 * Returns a function to escape an argument for use in csh when the argument is
 * being quoted.
 *
 * @returns {function(string): string} A function to escape arguments.
 */
function getQuoteEscapeFunction$2() {
  const controls = new RegExp(/[\0\u0008\r\u001B\u009B]/g);
  const newlines = new RegExp(/\n/g);
  const quotes = new RegExp(/'/g);
  const history = new RegExp(/!/g);
  return (arg) =>
    arg
      .replace(controls, "")
      .replace(newlines, " ")
      .replace(quotes, "'\\''")
      .replace(history, "\\!");
}

/**
 * Quotes an argument for use in csh.
 *
 * @param {string} arg The argument to quote.
 * @returns {string} The quoted argument.
 */
function quoteArg$4(arg) {
  return `'${arg}'`;
}

/**
 * Returns a pair of functions to escape and quote arguments for use in csh.
 *
 * @returns {(function(string): string)[]} A function pair to escape & quote arguments.
 */
function getQuoteFunction$6() {
  return [getQuoteEscapeFunction$2(), quoteArg$4];
}

var csh = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getEscapeFunction: getEscapeFunction$6,
  getQuoteFunction: getQuoteFunction$6
});

/**
 * @overview Provides functionality for the Debian Almquist shell (Dash).
 * @license MPL-2.0
 */

/**
 * Escape an argument for use in Dash.
 *
 * @param {string} arg The argument to escape.
 * @returns {string} The escaped argument.
 */
function escapeArg$1(arg) {
  return arg
    .replace(/[\0\u0008\r\u001B\u009B]/gu, "")
    .replace(/\n/gu, " ")
    .replace(/\\/gu, "\\\\")
    .replace(/(?<=^|\s)([#~])/gu, "\\$1")
    .replace(/(["$&'()*;<>?[\]`|])/gu, "\\$1")
    .replace(/([\t ])/gu, "\\$1");
}

/**
 * Returns a function to escape arguments for use in Dash for the given use
 * case.
 *
 * @returns {function(string): string} A function to escape arguments.
 */
function getEscapeFunction$5() {
  return escapeArg$1;
}

/**
 * Escape an argument for use in Dash when the argument is being quoted.
 *
 * @param {string} arg The argument to escape.
 * @returns {string} The escaped argument.
 */
function escapeArgForQuoted$1(arg) {
  return arg
    .replace(/[\0\u0008\u001B\u009B]/gu, "")
    .replace(/\r(?!\n)/gu, "")
    .replace(/'/gu, "'\\''");
}

/**
 * Quotes an argument for use in Dash.
 *
 * @param {string} arg The argument to quote.
 * @returns {string} The quoted argument.
 */
function quoteArg$3(arg) {
  return `'${arg}'`;
}

/**
 * Returns a pair of functions to escape and quote arguments for use in Dash.
 *
 * @returns {(function(string): string)[]} A function pair to escape & quote arguments.
 */
function getQuoteFunction$5() {
  return [escapeArgForQuoted$1, quoteArg$3];
}

var dash = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getEscapeFunction: getEscapeFunction$5,
  getQuoteFunction: getQuoteFunction$5
});

/**
 * @overview Provides functionality for shell-less escaping on Unix systems.
 * @license MPL-2.0
 */


/**
 * The error message for use of quoting functionality.
 *
 * @constant
 * @type {string}
 */
const unsupportedError$1 = "Quoting is not supported when no shell is used";

/**
 * Returns a function to escape arguments for shell-less use.
 *
 * @returns {function(string): string} A function to escape arguments.
 */
function getEscapeFunction$4() {
  const controls = new RegExp(/[\0\u0008\u001B\u009B]/g);
  const crs = new RegExp(/(\r\n)|\r/g);
  return (arg) => arg.replace(controls, "").replace(crs, "$1");
}

/**
 * Returns the provided value.
 *
 * @returns {never} Does not return.
 * @throws {Error} Always throws an error.
 */
function unsupported$1() {
  throw new Error(unsupportedError$1);
}

/**
 * Returns a pair of functions that will indicate this operation is unsupported.
 *
 * @returns {(function(string): string)[]} A pair of functions.
 */
function getQuoteFunction$4() {
  return [unsupported$1, unsupported$1];
}

var nosh$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getEscapeFunction: getEscapeFunction$4,
  getQuoteFunction: getQuoteFunction$4
});

/**
 * @overview Provides functionality for the Z shell (Zsh).
 * @license MPL-2.0
 */

/**
 * Escape an argument for use in Zsh.
 *
 * @param {string} arg The argument to escape.
 * @returns {string} The escaped argument.
 */
function escapeArg(arg) {
  return arg
    .replace(/[\0\u0008\r\u001B\u009B]/gu, "")
    .replace(/\n/gu, " ")
    .replace(/\\/gu, "\\\\")
    .replace(/(?<=^|\s)([#=~])/gu, "\\$1")
    .replace(/(["$&'()*;<>?[\]`{|}])/gu, "\\$1")
    .replace(/([\t ])/gu, "\\$1");
}

/**
 * Returns a function to escape arguments for use in Zsh for the given use case.
 *
 * @returns {function(string): string} A function to escape arguments.
 */
function getEscapeFunction$3() {
  return escapeArg;
}

/**
 * Escape an argument for use in Zsh when the argument is being quoted.
 *
 * @param {string} arg The argument to escape.
 * @returns {string} The escaped argument.
 */
function escapeArgForQuoted(arg) {
  return arg
    .replace(/[\0\u0008\u001B\u009B]/gu, "")
    .replace(/\r(?!\n)/gu, "")
    .replace(/'/gu, "'\\''");
}

/**
 * Quotes an argument for use in Zsh.
 *
 * @param {string} arg The argument to quote.
 * @returns {string} The quoted argument.
 */
function quoteArg$2(arg) {
  return `'${arg}'`;
}

/**
 * Returns a pair of functions to escape and quote arguments for use in Zsh.
 *
 * @returns {(function(string): string)[]} A function pair to escape & quote arguments.
 */
function getQuoteFunction$3() {
  return [escapeArgForQuoted, quoteArg$2];
}

var zsh = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getEscapeFunction: getEscapeFunction$3,
  getQuoteFunction: getQuoteFunction$3
});

/**
 * @overview Provides functionality for Unix systems.
 * @license MPL-2.0
 */


/**
 * The name of the Bourne-again shell (Bash) binary.
 *
 * @constant
 * @type {string}
 */
const binBash = "bash";

/**
 * The name of the BusyBox binary.
 *
 * @constant
 * @type {string}
 */
const binBusyBox = "busybox";

/**
 * The name of the C shell (csh) binary.
 *
 * @constant
 * @type {string}
 */
const binCsh = "csh";

/**
 * An alternative name for the C shell (csh) binary.
 *
 * @constant
 * @type {string}
 */
const binCshBsd = "bsd-csh";

/**
 * The name of the Debian Almquist shell (Dash) binary.
 *
 * @constant
 * @type {string}
 */
const binDash = "dash";

/**
 * The name of the Z shell (Zsh) binary.
 *
 * @constant
 * @type {string}
 */
const binZsh = "zsh";

/**
 * Returns the default shell for Unix systems.
 *
 * For more information, see `options.shell` in:
 * https://nodejs.org/api/child_process.html#child_processexeccommand-options-callback.
 *
 * @returns {string} The default shell.
 */
function getDefaultShell$1() {
  return "/bin/sh";
}

/**
 * Returns a function to enable protection against flag injection for Unix
 * systems.
 *
 * @returns {function(string): string[]} A function enabling flag protection.
 */
function getFlagFunction$1() {
  const splitter = /(?<!-)(-+)/;
  return (arg) => arg.split(splitter);
}

/**
 * Returns the helper functions to handle arguments for use with a particular
 * shell.
 *
 * @param {string | symbol} shellName The identifier of a Unix shell.
 * @returns {object} A set of functions to escape arguments.
 */
function getShellHelpers$1(shellName) {
  switch (shellName) {
    case noShell: {
      return nosh$1;
    }
    case binBash: {
      return bash;
    }
    case binBusyBox: {
      return busybox;
    }
    case binCsh:
    case binCshBsd: {
      return csh;
    }
    case binDash: {
      return dash;
    }
    case binZsh: {
      return zsh;
    }
  }
}

/**
 * Determines the name of the shell identified by a file path or file name.
 *
 * @param {object} args The arguments for this function.
 * @param {Object<string, string>} args.env The environment variables.
 * @param {string} args.shell The name or path of the shell.
 * @param {object} deps The dependencies for this function.
 * @param {function(): string} deps.resolveExecutable Resolve the path to an executable.
 * @returns {string} The shell name.
 */
function getShellName$1({ env, shell }, { resolveExecutable }) {
  shell = resolveExecutable(
    { env, executable: shell },
    { exists: existsSync, readlink: readlinkSync, which: which.sync },
  );

  const shellName = path__namespace.basename(shell);
  return shellName;
}

/**
 * Checks if the given shell is supported on Unix or not.
 *
 * @param {string} shellName The name of a Unix shell.
 * @returns {boolean} `true` if the shell is supported, `false` otherwise.
 */
function isShellSupported$1(shellName) {
  return getShellHelpers$1(shellName) !== undefined;
}

var unix = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getDefaultShell: getDefaultShell$1,
  getFlagFunction: getFlagFunction$1,
  getShellHelpers: getShellHelpers$1,
  getShellName: getShellName$1,
  isShellSupported: isShellSupported$1
});

/**
 * @overview Provides functionality for the Windows Command Prompt.
 * @license MPL-2.0
 */


/**
 * Returns a function to escape arguments for use in CMD for the given use case.
 *
 * @returns {function(string): string} A function to escape arguments.
 */
function getEscapeFunction$2() {
  const controls = new RegExp(/[\0\u0008\r\u001B\u009B]/g);
  const newlines = new RegExp(/\n/g);
  const specials = new RegExp(/([%&<>^|])/g);
  const quotes = new RegExp(/"/g);
  const backslashes = new RegExp(/(^|[^\\])(\\*)\0/g);
  return (arg) =>
    arg
      .replace(controls, "")
      .replace(newlines, " ")
      .replace(specials, "^$1")
      .replace(quotes, '\0\\^"')
      .replace(backslashes, "$1$2$2");
}

/**
 * Escape an argument for use in CMD when the argument is being quoted.
 *
 * @returns {function(string): string} A function to escape arguments.
 */
function getQuoteEscapeFunction$1() {
  const controls = new RegExp(/[\0\u0008\r\u001B\u009B]/g);
  const newlines = new RegExp(/\n/g);
  const quotes = new RegExp(/"/g);
  const specials = new RegExp(/([%&<>^|])/g);
  const backslashes = new RegExp(/(^|[^\\])(\\+)("|$)/g);
  return (arg) =>
    arg
      .replace(controls, "")
      .replace(newlines, " ")
      .replace(quotes, '""')
      .replace(specials, '"^$1"')
      .replace(backslashes, "$1$2$2$3");
}

/**
 * Quotes an argument for use in CMD.
 *
 * @param {string} arg The argument to quote.
 * @returns {string} The quoted argument.
 */
function quoteArg$1(arg) {
  return `"${arg}"`;
}

/**
 * Returns a pair of functions to escape and quote arguments for use in CMD.
 *
 * @returns {(function(string): string)[]} A function pair to escape & quote arguments.
 */
function getQuoteFunction$2() {
  return [getQuoteEscapeFunction$1(), quoteArg$1];
}

var cmd = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getEscapeFunction: getEscapeFunction$2,
  getQuoteFunction: getQuoteFunction$2
});

/**
 * @overview Provides functionality for shell-less escaping on Windows systems.
 * @license MPL-2.0
 */


/**
 * The error message for use of quoting functionality.
 *
 * @constant
 * @type {string}
 */
const unsupportedError = "Quoting is not supported when no shell is used";

/**
 * Returns a function to escape arguments for shell-less use.
 *
 * @returns {function(string): string} A function to escape arguments.
 */
function getEscapeFunction$1() {
  const controls = new RegExp(/[\0\u0008\u001B\u009B]/g);
  const crs = new RegExp(/(\r\n)|\r/g);
  return (arg) => arg.replace(controls, "").replace(crs, "$1");
}

/**
 * Returns the provided value.
 *
 * @returns {never} Does not return.
 * @throws {Error} Always throws an error.
 */
function unsupported() {
  throw new Error(unsupportedError);
}

/**
 * Returns a pair of functions that will indicate this operation is unsupported.
 *
 * @returns {(function(string): string)[]} A pair of functions.
 */
function getQuoteFunction$1() {
  return [unsupported, unsupported];
}

var nosh = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getEscapeFunction: getEscapeFunction$1,
  getQuoteFunction: getQuoteFunction$1
});

/**
 * @overview Provides functionality for Windows PowerShell.
 * @license MPL-2.0
 */


/**
 * Returns a function to escape arguments for use in PowerShell for the given
 * use case.
 *
 * @returns {function(string): string} A function to escape arguments.
 */
function getEscapeFunction() {
  const controls = new RegExp(/[\0\u0008\r\u001B\u009B]/g);
  const newlines = new RegExp(/\n/g);
  const backticks = new RegExp(/`/g);
  const redirects = new RegExp(/(^|[\s\u0085])([*1-6]?)(>)/g);
  const specials1 = new RegExp(/(^|[\s\u0085])([#\-:<@\]])/g);
  const specials2 = new RegExp(/([$&'(),;{|}‘’‚‛“”„])/g);

  const whitespace = new RegExp(/([\s\u0085])/g);
  const whitespacePrefix = new RegExp(/^[\s\u0085]+/);

  const quote = new RegExp('"', "g");
  const backslashBeforeQuote = new RegExp(/(^|[^\\])(\\*)\0/g);

  const backslashSuffix = new RegExp(/([^\\])(\\+)$/);

  return (arg) => {
    arg = arg
      .replace(controls, "")
      .replace(newlines, " ")
      .replace(backticks, "``")
      .replace(redirects, "$1$2`$3")
      .replace(specials1, "$1`$2")
      .replace(specials2, "`$1");

    if (whitespace.test(arg.replace(whitespacePrefix, ""))) {
      arg = arg
        .replace(quote, '\0`"`"')
        .replace(backslashBeforeQuote, "$1$2$2")
        .replace(backslashSuffix, "$1$2$2");
    } else {
      arg = arg
        .replace(quote, '\0\\`"')
        .replace(backslashBeforeQuote, "$1$2$2");
    }

    arg = arg.replace(whitespace, "`$1");

    return arg;
  };
}

/**
 * Escape an argument for use in PowerShell when the argument is being quoted.
 *
 * @returns {function(string): string} A function to escape arguments.
 */
function getQuoteEscapeFunction() {
  const controls = new RegExp(/[\0\u0008\u001B\u009B]/g);
  const crs = new RegExp(/(\r\n)|\r/g);
  const quotes = new RegExp(/(['‘’‚‛])/g);

  const whitespace = new RegExp(/[\s\u0085]/);

  const quote = new RegExp('"', "g");
  const backslashBeforeQuote = new RegExp(/(^|[^\\])(\\*)\0/g);

  const backslashSuffix = new RegExp(/([^\\])(\\+)$/);

  return (arg) => {
    arg = arg.replace(controls, "").replace(crs, "$1").replace(quotes, "$1$1");

    if (whitespace.test(arg)) {
      arg = arg
        .replace(quote, '\0""')
        .replace(backslashBeforeQuote, "$1$2$2")
        .replace(backslashSuffix, "$1$2$2");
    } else {
      arg = arg.replace(quote, '\0\\"').replace(backslashBeforeQuote, "$1$2$2");
    }

    return arg;
  };
}

/**
 * Quotes an argument for use in PowerShell.
 *
 * @param {string} arg The argument to quote and escape.
 * @returns {string} The quoted and escaped argument.
 */
function quoteArg(arg) {
  return `'${arg}'`;
}

/**
 * Returns a pair of functions to escape and quote arguments for use in
 * PowerShell.
 *
 * @returns {(function(string): string)[]} A function pair to escape & quote arguments.
 */
function getQuoteFunction() {
  return [getQuoteEscapeFunction(), quoteArg];
}

var powershell = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getEscapeFunction: getEscapeFunction,
  getQuoteFunction: getQuoteFunction
});

/**
 * @overview Provides functionality for Windows systems.
 * @license MPL-2.0
 */


/**
 * The name of the Windows Command Prompt binary.
 *
 * @constant
 * @type {string}
 */
const binCmd = "cmd.exe";

/**
 * The name of the Windows PowerShell binary.
 *
 * @constant
 * @type {string}
 */
const binPowerShell = "powershell.exe";

/**
 * Returns the default shell for Windows systems.
 *
 * For more information, see:
 * https://nodejs.org/api/child_process.html#default-windows-shell.
 *
 * @param {object} args The arguments for this function.
 * @param {Object<string, string>} args.env The environment variables.
 * @returns {string} The default shell.
 */
function getDefaultShell({ env }) {
  const ComSpec = hasOwn(env, "ComSpec") ? env.ComSpec : undefined;
  if (ComSpec !== undefined) {
    return ComSpec;
  }

  return binCmd;
}

/**
 * Returns a function to enable protection against flag injection for Windows
 * systems.
 *
 * @returns {function(string): string[]} A function enabling flag protection.
 */
function getFlagFunction() {
  const splitter = /(?<![-/])([-/]+)/;
  return (arg) => arg.split(splitter);
}

/**
 * Returns the helper functions to handle arguments for use with a particular
 * shell.
 *
 * @param {string | symbol} shellName The identifier of a Windows shell.
 * @returns {object} A set of functions to escape arguments.
 */
function getShellHelpers(shellName) {
  if (shellName === noShell) {
    return nosh;
  }

  switch (shellName.toLowerCase()) {
    case binCmd: {
      return cmd;
    }
    case binPowerShell: {
      return powershell;
    }
  }
}

/**
 * Determines the name of the shell identified by a file path or file name.
 *
 * @param {object} args The arguments for this function.
 * @param {Object<string, string>} args.env The environment variables.
 * @param {string} args.shell The name or path of the shell.
 * @param {object} deps The dependencies for this function.
 * @param {function(): string} deps.resolveExecutable Resolve the path to an executable.
 * @returns {string} The shell name.
 */
function getShellName({ env, shell }, { resolveExecutable }) {
  shell = resolveExecutable(
    { env, executable: shell },
    { exists: existsSync, readlink: readlinkSync, which: which.sync },
  );

  const shellName = path__namespace.win32.basename(shell);
  return shellName;
}

/**
 * Checks if the given shell is supported on Windows or not.
 *
 * @param {string} shellName The name of a Windows shell.
 * @returns {boolean} `true` if the shell is supported, `false` otherwise.
 */
function isShellSupported(shellName) {
  return getShellHelpers(shellName) !== undefined;
}

var win = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getDefaultShell: getDefaultShell,
  getFlagFunction: getFlagFunction,
  getShellHelpers: getShellHelpers,
  getShellName: getShellName,
  isShellSupported: isShellSupported
});

/**
 * @overview Provides functionality related to getting the platform module for
 * the current system.
 * @license MPL-2.0
 */


/**
 * The string identifying the OS type Cygwin.
 *
 * @constant
 * @type {string}
 */
const cygwin = "cygwin";

/**
 * The string identifying the OS type MSYS.
 *
 * @constant
 * @type {string}
 */
const msys = "msys";

/**
 * The string identifying Windows platforms.
 *
 * @constant
 * @type {string}
 */
const win32 = "win32";

/**
 * Checks if the current system is a Windows system.
 *
 * @param {object} args The arguments for this function.
 * @param {Object<string, string>} args.env The environment variables.
 * @param {string} args.platform The `os.platform()` value.
 * @returns {boolean} `true` if the system is Windows, `false` otherwise.
 */
function isWindow({ env, platform }) {
  const osType = hasOwn(env, "OSTYPE") ? env.OSTYPE : undefined;
  return osType === cygwin || osType === msys || platform === win32;
}

/**
 * Returns all helper functions for a specific system.
 *
 * @param {object} args The arguments for this function.
 * @param {Object<string, string>} args.env The environment variables.
 * @param {string} args.platform The `os.platform()` value.
 * @returns {object} The helper functions for the current system.
 */
function getHelpersByPlatform({ env, platform }) {
  if (isWindow({ env, platform })) {
    return win;
  }

  return unix;
}

/**
 * A simple shell escape library. Use it to escape user-controlled inputs to
 * shell commands to prevent shell injection.
 *
 * @overview Entrypoint for the library.
 * @module shescape
 * @version 2.1.11
 * @license MPL-2.0
 */


/**
 * A class to escape user-controlled inputs to shell commands to prevent shell
 * injection.
 *
 * @example
 * import { spawn } from "node:child_process";
 * const shescape = new Shescape({ shell: false });
 * spawn(
 *   "echo",
 *   ["Hello", shescape.escape(userInput)],
 *   null // `options.shell` MUST be falsy
 * );
 * @example
 * import { spawn } from "node:child_process";
 * const shescape = new Shescape({ shell: false });
 * spawn(
 *   "echo",
 *   shescape.escapeAll(["Hello", userInput]),
 *   null // `options.shell` MUST be falsy
 * );
 * @example
 * import { spawn } from "node:child_process";
 * const spawnOptions = { shell: true }; // `options.shell` SHOULD be truthy
 * const shescape = new Shescape({ shell: spawnOptions.shell });
 * spawn(
 *   "echo",
 *   ["Hello", shescape.quote(userInput)],
 *   spawnOptions
 * );
 * @example
 * import { spawn } from "node:child_process";
 * const spawnOptions = { shell: true }; // `options.shell` SHOULD be truthy
 * const shescape = new Shescape({ shell: spawnOptions.shell });
 * spawn(
 *   "echo",
 *   shescape.quoteAll(["Hello", userInput]),
 *   spawnOptions
 * );
 */
class Shescape {
  #escape;
  #quote;

  /**
   * Create a new {@link Shescape} instance.
   *
   * @param {object} [options] The escape options.
   * @param {boolean} [options.flagProtection=true] Is flag protection enabled.
   * @param {boolean | string} [options.shell=true] The shell to escape for.
   * @throws {Error} The shell is not supported or could not be found.
   * @since 2.0.0
   */
  constructor(options = {}) {
    const platform = getHelpersByPlatform({
      env: process.env,
      platform: os.platform(),
    });

    options = parseOptions(
      { env: process.env, options, version: process.version },
      platform,
    );
    const { flagProtection, shellName } = options;

    const shell = platform.getShellHelpers(shellName);
    const flagFn = flagProtection ? platform.getFlagFunction() : undefined;

    {
      const escapeFn = shell.getEscapeFunction();
      this.#escape = compose({ escapeFn, flagFn });
    }

    {
      const [escapeFn, quoteFn] = shell.getQuoteFunction();
      this.#quote = compose({ escapeFn, flagFn, quoteFn });
    }
  }

  /**
   * Take a single value, the argument, and escape any dangerous characters.
   *
   * Non-string inputs will be converted to strings using a `toString()` method.
   *
   * @param {string} arg The argument to escape.
   * @returns {string} The escaped argument.
   * @throws {TypeError} The argument is not stringable.
   * @since 2.0.0
   */
  escape(arg) {
    const argAsString = checkedToString(arg);
    return this.#escape(argAsString);
  }

  /**
   * Take an array of values, the arguments, and escape any dangerous characters
   * in every argument.
   *
   * Non-array inputs are rejected. Non-string entries will be converted to
   * strings using a `toString()` method.
   *
   * @param {string[]} args The arguments to escape.
   * @returns {string[]} The escaped arguments.
   * @throws {TypeError} The arguments are not an array.
   * @throws {TypeError} One of the arguments is not stringable.
   * @since 2.0.0
   */
  escapeAll(args) {
    ensureArray(args);
    return args.map((arg) => this.escape(arg));
  }

  /**
   * Take a single value, the argument, put shell-specific quotes around it and
   * escape any dangerous characters.
   *
   * Non-string inputs will be converted to strings using a `toString()` method.
   *
   * @param {string} arg The argument to quote and escape.
   * @returns {string} The quoted and escaped argument.
   * @throws {TypeError} The argument is not stringable.
   * @throws {Error} Quoting is not supported with `shell: false`.
   * @since 2.0.0
   */
  quote(arg) {
    const argAsString = checkedToString(arg);
    return this.#quote(argAsString);
  }

  /**
   * Take an array of values, the arguments, put shell-specific quotes around
   * every argument and escape any dangerous characters in every argument.
   *
   * Non-array inputs are rejected. Non-string entries will be converted to
   * strings using a `toString()` method.
   *
   * @param {string[]} args The arguments to quote and escape.
   * @returns {string[]} The quoted and escaped arguments.
   * @throws {TypeError} The arguments are not an array.
   * @throws {TypeError} One of the arguments is not stringable.
   * @throws {Error} Quoting is not supported with `shell: false`.
   * @since 2.0.0
   */
  quoteAll(args) {
    ensureArray(args);
    return args.map((arg) => this.quote(arg));
  }
}

/**
 * @overview Alternative entrypoint for the library that provides a stateless
 * API.
 * @license MPL-2.0
 */


/**
 * Take a single value, the argument, and escape any dangerous characters.
 *
 * Non-string inputs will be converted to strings using a `toString()` method.
 *
 * @example
 * import { spawn } from "node:child_process";
 * spawn(
 *   "echo",
 *   ["Hello", shescape.escape(userInput, { shell: false })],
 *   null // `options.shell` MUST be falsy
 * );
 * @param {string} arg The argument to escape.
 * @param {object} [options] The options for escaping.
 * @param {boolean} [options.flagProtection=true] Is flag protection enabled.
 * @param {boolean | string} [options.shell=true] The shell to escape for.
 * @returns {string} The escaped argument.
 * @throws {TypeError} The argument is not stringable.
 * @throws {Error} The shell is not supported or could not be found.
 * @since 2.1.0
 */
function escape(arg, options) {
  const shescape = new Shescape(options);
  return shescape.escape(arg);
}

/**
 * Take an array of values, the arguments, and escape any dangerous characters
 * in every argument.
 *
 * Non-array inputs are rejected. Non-string entries will be converted to
 * strings using a `toString()` method.
 *
 * @example
 * import { spawn } from "node:child_process";
 * spawn(
 *   "echo",
 *   shescape.escapeAll(["Hello", userInput], { shell: false }),
 *   null // `options.shell` MUST be falsy
 * );
 * @param {string[]} args The arguments to escape.
 * @param {object} [options] The options for escaping.
 * @param {boolean} [options.flagProtection=true] Is flag protection enabled.
 * @param {boolean | string} [options.shell=true] The shell to escape for.
 * @returns {string[]} The escaped arguments.
 * @throws {TypeError} The arguments are not an array.
 * @throws {TypeError} One of the arguments is not stringable.
 * @throws {Error} The shell is not supported or could not be found.
 * @since 2.1.0
 */
function escapeAll(args, options) {
  const shescape = new Shescape(options);
  return shescape.escapeAll(args);
}

/**
 * Take a single value, the argument, put shell-specific quotes around it and
 * escape any dangerous characters.
 *
 * Non-string inputs will be converted to strings using a `toString()` method.
 *
 * @example
 * import { spawn } from "node:child_process";
 * const spawnOptions = { shell: true }; // `options.shell` SHOULD be truthy
 * spawn(
 *   "echo",
 *   ["Hello", shescape.quote(userInput, { shell: spawnOptions.shell })],
 *   spawnOptions
 * );
 * @param {string} arg The argument to quote and escape.
 * @param {object} [options] The options for escaping.
 * @param {boolean} [options.flagProtection=true] Is flag protection enabled.
 * @param {boolean | string} [options.shell=true] The shell to escape for.
 * @returns {string} The quoted and escaped argument.
 * @throws {TypeError} The argument is not stringable.
 * @throws {Error} The shell is not supported or could not be found.
 * @throws {Error} Quoting is not supported with `shell: false`.
 * @since 2.1.0
 */
function quote(arg, options) {
  const shescape = new Shescape(options);
  return shescape.quote(arg);
}

/**
 * Take an array of values, the arguments, put shell-specific quotes around
 * every argument and escape any dangerous characters in every argument.
 *
 * Non-array inputs are rejected. Non-string entries will be converted to
 * strings using a `toString()` method.
 *
 * @example
 * import { spawn } from "node:child_process";
 * const spawnOptions = { shell: true }; // `options.shell` SHOULD be truthy
 * spawn(
 *   "echo",
 *   shescape.quoteAll(["Hello", userInput], { shell: spawnOptions.shell }),
 *   spawnOptions
 * );
 * @param {string[]} args The arguments to quote and escape.
 * @param {object} [options] The options for escaping.
 * @param {boolean} [options.flagProtection=true] Is flag protection enabled.
 * @param {boolean | string} [options.shell=true] The shell to escape for.
 * @returns {string[]} The quoted and escaped arguments.
 * @throws {TypeError} The arguments are not an array.
 * @throws {TypeError} One of the arguments is not stringable.
 * @throws {Error} The shell is not supported or could not be found.
 * @throws {Error} Quoting is not supported with `shell: false`.
 * @since 2.1.0
 */
function quoteAll(args, options) {
  const shescape = new Shescape(options);
  return shescape.quoteAll(args);
}

exports.escape = escape;
exports.escapeAll = escapeAll;
exports.quote = quote;
exports.quoteAll = quoteAll;
